/**
 * /portal/reports — Pro-only progression report list (PR-21, PR-22). 404 for
 * Studio per ui-structure.md §Route map.
 *
 * The Pro progression-report renderer (AI-10) and the per-report route
 * (/portal/reports/$reportId) are out of Phase G scope (T-036 through
 * T-046). Phase G ships the Pro-gated list shell so the nav link is valid and
 * the surface is invisible to Studio.
 */

import { useEffect, useState } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { supabase } from '@/lib/supabase';
import { AsyncPanel, type AsyncState } from '@/components/portal/AsyncPanel';

interface ReportRow {
  id: string;
  period_start: string;
  period_end: string;
  delivered_at: string | null;
}

export const Route = createFileRoute('/portal/reports/')({
  beforeLoad: ({ context }) => {
    const portal = (context as { portal?: { isOperator: boolean; tier: string | null } }).portal;
    const allowed = portal?.isOperator || portal?.tier === 'pro';
    if (!allowed) throw notFound();
  },
  component: ReportsPage,
});

function ReportsPage() {
  const [state, setState] = useState<AsyncState<ReportRow[]>>({
    status: 'loading',
  });

  useEffect(() => {
    let cancelled = false;
    supabase
      .from('progression_reports')
      .select('id, period_start, period_end, delivered_at')
      .order('period_start', { ascending: false })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setState({
            status: 'error',
            message: 'Could not load your reports. Try again in a moment.',
          });
          return;
        }
        setState({ status: 'ready', data: (data as ReportRow[]) ?? [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="mb-1 font-display text-2xl text-[color:var(--color-navy)]">
          Progression reports
        </h1>
        <p className="text-sm text-[color:var(--color-text-mute)]">
          A monthly read on where the work moved you. Pro only.
        </p>
      </div>
      <AsyncPanel state={state} loadingLabel="Loading your reports...">
        {(reports) =>
          reports.length === 0 ? (
            <p className="text-sm text-[color:var(--color-text-mute)]">
              Your first progression report lands after a full month of work.
            </p>
          ) : (
            <ul className="space-y-3">
              {reports.map((r) => (
                <li
                  key={r.id}
                  className="rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-white)] p-4"
                >
                  <p className="text-sm font-medium text-[color:var(--color-navy)]">
                    {formatPeriod(r.period_start, r.period_end)}
                  </p>
                  <p className="text-xs text-[color:var(--color-text-mute)]">
                    {r.delivered_at
                      ? 'Delivered'
                      : 'Being prepared'}
                  </p>
                </li>
              ))}
            </ul>
          )
        }
      </AsyncPanel>
    </div>
  );
}

function formatPeriod(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' };
  const s = new Date(start).toLocaleDateString('en-US', opts);
  const e = new Date(end).toLocaleDateString('en-US', opts);
  return s === e ? s : `${s} to ${e}`;
}
